/**
 * Canvas — v2 wrapper around v1's vendored canvas stack.
 *
 * Mimics v1 exactly via the @console alias: same useYDoc hook, same
 * useYCanvas wiring, same nodeTypes/edgeTypes registry, same ReactFlow
 * config. No v1 changes; v2 imports them as a shared library.
 *
 * The visual identity of individual nodes (CharacterDesignerNode,
 * LocationDesignerNode, StoryboardDesignerNode, etc.) stays exactly
 * as v1 — Phase 3.3+ can theme them with v2 design tokens; for now
 * full functional parity is the priority over per-node reskin.
 *
 * Mounts two React contexts that the vendored nodes need:
 *   - CanvasContext: addNode / addEdge / writeNodeData / replacePortEdges
 *     etc. The vendored ImageUploadNode + ReferenceSelector reach for
 *     `useCanvas()` to manipulate the graph from inside their bottom
 *     panel. Without it, the "Add reference image" button silently
 *     no-ops in v2 (it works in v1 because WorkspacePageV2 mounts the
 *     provider).
 *   - UploadContext: the deeper ReferenceSelector → ImagePickerModal
 *     chain calls `useUploadFile()(file, 'image')` to spawn an `image`
 *     upload node and wire an edge. We implement the handler with the
 *     same shape as v1's `handleUploadFromRefSelector` so the vendored
 *     UI flows end-to-end.
 *
 * v1 is NOT modified — both contexts are additive in v2.
 */
import { useCallback, useMemo, useRef, useState } from 'react'
import * as Y from 'yjs'
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type NodeChange,
  type ReactFlowInstance,
} from 'reactflow'
import 'reactflow/dist/style.css'

// Vendored v1 — same source, no v1 changes.
//
// IMPORT ORDER MATTERS: nodeRegistry / canvas-v2 barrel must be imported
// BEFORE useYCanvas. There's a real circular dep between nodeRegistry
// and GroupNode (GroupNode imports `nodeSchemas` from nodeRegistry at
// module top-level). v1's bundle resolves the cycle correctly because
// WorkspacePageV2 imports through the barrel first; importing useYCanvas
// first triggers a deep-path import of GroupNode.tsx that races
// nodeRegistry's evaluation and crashes with "Cannot access 'GroupNode'
// before initialization".
import { nodeTypes, edgeTypes, getAvailableNodeTypes } from '@console/components/canvas-v2'
import { createNode, nodeSchemas } from '@console/components/canvas-v2/nodeRegistry'
import { areTypesCompatible } from '@console/components/canvas-v2/portTypes'
import { computeParentGroupCleanup } from '@console/components/canvas-v2/nodes/GroupNode/parentGroupCleanup'
import { collectDownstreamAffectedByRemoval } from '@console/components/canvas-v2/collectDownstreamAffectedByRemoval'
import { VersionsPanel } from './VersionsPanel'
import {
  CanvasContext,
  type CanvasContextValue,
  type CanvasPosition,
} from '@console/components/canvas-v2/CanvasContext'
import { UploadContext, type UploadFileFn } from '@console/contexts/UploadContext'
import {
  useYCanvas,
  type ReactFlowCanvasNode,
  type ReactFlowCanvasEdge,
  type ReactFlowNodeData,
  type ReactFlowCanvasEdgeData,
} from '@console/hooks/useYCanvas'
import { executeNode as executeNodeApi } from '@ponpon/api-client'

// Built once — fed to useYCanvas so it skips YDoc nodes whose type is
// not in the registry (matches v1's filter behavior).
const KNOWN_NODE_TYPES = new Set(getAvailableNodeTypes())

export function Canvas({
  projectId, ydoc, synced, connected,
}: { projectId: string; ydoc: Y.Doc; synced: boolean; connected: boolean }) {
  // 2. ReactFlow state — useYCanvas writes into these.
  const [nodes, setNodes, onNodesChangeBase] = useNodesState<ReactFlowNodeData>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<ReactFlowCanvasEdgeData>([])
  const nodesRef = useRef<ReactFlowCanvasNode[]>([])
  const edgesRef = useRef<ReactFlowCanvasEdge[]>([])
  nodesRef.current = nodes as ReactFlowCanvasNode[]
  edgesRef.current = edges as ReactFlowCanvasEdge[]

  const [versionsPanelOpen, setVersionsPanelOpen] = useState(false)
  const isUndoRedo = useRef(false)
  const reactFlowInstanceRef = useRef<ReactFlowInstance<ReactFlowNodeData, ReactFlowCanvasEdgeData> | null>(null)
  const selectedNodeIdsRef = useRef<Set<string>>(new Set())

  const onInit = useCallback((rf: ReactFlowInstance<ReactFlowNodeData, ReactFlowCanvasEdgeData>) => {
    reactFlowInstanceRef.current = rf
  }, [])

  // 3. Wire YDoc → ReactFlow state (and writes back, e.g. node positions).
  const {
    ready,
    ydocRunningCount,
    addNodeToYDoc,
    removeNodeFromYDoc,
    addEdgeToYDoc,
    removeEdgeFromYDoc,
    writeNodeData,
    writeNodePosition,
    writeNodeDimensions,
    flushNodeInputFromEdgeRefs,
  } = useYCanvas({
    ydoc,
    synced,
    setNodes: setNodes as unknown as (
      n: ReactFlowCanvasNode[] | ((p: ReactFlowCanvasNode[]) => ReactFlowCanvasNode[]),
    ) => void,
    setEdges: setEdges as unknown as (
      e: ReactFlowCanvasEdge[] | ((p: ReactFlowCanvasEdge[]) => ReactFlowCanvasEdge[]),
    ) => void,
    nodesRef,
    edgesRef,
    isBackendConnected: connected && synced,
    isUndoRedo,
    reactFlowInstance: reactFlowInstanceRef,
    knownNodeTypes: KNOWN_NODE_TYPES,
    selectedNodeIdsRef,
    projectId,
  })

  // v1 parity (apps/console/src/pages/WorkspacePageV2.tsx onNodesChange):
  // wrap the bare ReactFlow handler so we can react to five change
  // types that v1 forwards:
  //
  //   1. ``dimensions`` — push the measured DOM width/height back to YDoc
  //      via writeNodeDimensions. Without this, group containers render
  //      at whatever stale ``style.height`` YDoc holds, surfacing as the
  //      c4a0ba45 row-overlap / huge-gap regression (commit c7687fa).
  //
  //   2. ``remove`` of a grouped child — null out the removed id in the
  //      parent group's ``in_child_node_ids`` and trim trailing nulls so
  //      the slot doesn't outlive the child. Without this, deleting a
  //      clip from a video_group leaves a stale id pointer that causes
  //      ghost slots until the next FE rebuild. (v1 lines 1906-1949.)
  //
  //   3. ``remove`` of any node — remove from YDoc so the deletion isn't
  //      resurrected on the next rebuild() pass.
  //
  //   4. ``select`` — sync ``selectedNodeIdsRef`` so consumers
  //      (toolbar visibility, future chat panel "send N nodes to chat"
  //      action) read a fresh selection set.
  //
  //   5. ``remove`` with outgoing edges — flush the downstream nodes'
  //      resolved port data so a deleted upstream's stale values don't
  //      sit on downstream inputs until the next regen. Mirrors v1's
  //      flushResolvedPortData path.
  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Collect removals first so we can compute parent-group updates
      // BEFORE letting the base handler mutate ReactFlow state. Reading
      // parentId AFTER onNodesChangeBase would race the removal.
      const removedIds = changes
        .filter((c): c is Extract<NodeChange, { type: 'remove' }> => c.type === 'remove')
        .map((c) => c.id)

      // Logic lives in @console/components/canvas-v2/nodes/GroupNode/parentGroupCleanup
      // so it can be unit-tested as a pure function. See that file for
      // the rules (trim trailing nulls, preserve middle nulls, only
      // act on type=nodeGroup parents).
      const parentUpdates = computeParentGroupCleanup(removedIds, nodesRef.current)

      // Snapshot the EDGE state pre-removal so the port-flush below knows
      // which downstream ports were fed by edges from the removed nodes.
      // Once onNodesChangeBase mutates state, ReactFlow may also cascade
      // edge removals — reading edgesRef after that would lose the topology.
      const affectedDownstream =
        removedIds.length > 0
          ? collectDownstreamAffectedByRemoval(removedIds, edgesRef.current)
          : []

      onNodesChangeBase(changes)

      // Apply parent updates to BOTH ReactFlow state and YDoc so the
      // grouped-children layout doesn't keep rendering a placeholder
      // slot for the deleted child. The YDoc write is what survives a
      // page reload; the local setNodes is what keeps this paint frame
      // correct.
      if (parentUpdates.size > 0) {
        setNodes((nds) =>
          nds.map((n) => {
            const newChildIds = parentUpdates.get(n.id)
            if (newChildIds === undefined) return n
            return { ...n, data: { ...n.data, in_child_node_ids: newChildIds } }
          }),
        )
        for (const [parentId, newChildIds] of parentUpdates) {
          writeNodeData(parentId, { in_child_node_ids: newChildIds })
        }
      }

      // Selection sync — keep selectedNodeIdsRef in lockstep with the
      // ReactFlow store. Read AFTER onNodesChangeBase so the ref reflects
      // the just-processed select events.
      const hasSelectChange = changes.some((c) => c.type === 'select')
      if (hasSelectChange) {
        const next = new Set<string>()
        for (const n of nodesRef.current) {
          if (n.selected) next.add(n.id)
        }
        selectedNodeIdsRef.current = next
      }

      for (const c of changes) {
        if (c.type === 'dimensions' && c.dimensions) {
          writeNodeDimensions(c.id, c.dimensions.width, c.dimensions.height)
        } else if (c.type === 'remove') {
          removeNodeFromYDoc(c.id)
        }
      }

      // Port flush — for every downstream port that lost an upstream
      // edge during this batch, re-resolve its value against the
      // remaining edges so stale values don't sit on the downstream
      // node's input until the next regen.
      if (affectedDownstream.length > 0) {
        const removedIdSet = new Set(removedIds)
        const remainingEdges = edgesRef.current.filter(
          (e) => !removedIdSet.has(e.source) && !removedIdSet.has(e.target),
        )
        const nodesSnapshot = nodesRef.current
        for (const { target, targetHandle } of affectedDownstream) {
          if (!targetHandle) continue  // flush helper requires a named handle
          const edgeRefs = remainingEdges
            .filter((e) => e.target === target && (e.targetHandle ?? undefined) === targetHandle)
            .map((e) => ({
              source: e.source,
              sourceHandle: e.sourceHandle ?? undefined,
            }))
          flushNodeInputFromEdgeRefs(target, targetHandle, nodesSnapshot, edgeRefs)
        }
      }
    },
    [
      onNodesChangeBase,
      setNodes,
      writeNodeData,
      writeNodeDimensions,
      removeNodeFromYDoc,
      flushNodeInputFromEdgeRefs,
    ],
  )

  return (
    <div className="relative w-full h-full bg-bg">
      {/* Status pill */}
      <div className="absolute top-3 right-3 z-10">
        <ConnectionPill
          connected={connected}
          synced={synced}
          ready={ready}
          nodeCount={nodes.length}
          running={ydocRunningCount}
        />
      </div>

      {/* Versions toolbar button — top-left, opens the VersionsPanel
          drawer. The drawer floats over the canvas (doesn't collapse it)
          so the user can see what they're about to restore. */}
      <button
        type="button"
        onClick={() => setVersionsPanelOpen((v) => !v)}
        className="absolute top-3 left-3 z-10 px-3 py-1.5 rounded-md border border-line bg-bg-1/90 backdrop-blur-sm text-[0.78rem] text-text-2 hover:text-text hover:border-text-3 transition-colors"
        title="Canvas versions — save / restore / delete snapshots"
      >
        {versionsPanelOpen ? 'Hide versions' : 'Versions'}
      </button>

      {versionsPanelOpen && (
        <VersionsPanel projectId={projectId} onClose={() => setVersionsPanelOpen(false)} />
      )}

      {/* Empty state — only when sync is complete and no nodes exist */}
      {ready && nodes.length === 0 && (
        <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none">
          <div className="text-center max-w-md px-6">
            <div className="t-mono text-[0.68rem] uppercase tracking-[0.22em] text-text-3 mb-3">
              empty canvas
            </div>
            <div
              className="text-text-2 text-[2.2rem] leading-tight"
              style={{ fontFamily: 'var(--font-italic)', fontStyle: 'italic' }}
            >
              Nothing here yet.
            </div>
            <p className="text-text-3 text-[0.96rem] mt-2">
              Ask the Director — describe the scene, add a character, write
              the first chapter. Nodes appear as they're created.
            </p>
          </div>
        </div>
      )}

      <ReactFlowProvider>
        <CanvasProviders
          projectId={projectId}
          nodesRef={nodesRef}
          setNodes={setNodes}
          addNodeToYDoc={addNodeToYDoc}
          removeNodeFromYDoc={removeNodeFromYDoc}
          addEdgeToYDoc={addEdgeToYDoc}
          removeEdgeFromYDoc={removeEdgeFromYDoc}
          writeNodeData={writeNodeData}
          writeNodePosition={writeNodePosition}
          ydoc={ydoc}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onInit={onInit}
            fitView
            fitViewOptions={{ padding: 0.25, minZoom: 0.15, maxZoom: 1.4 }}
            minZoom={0.05}
            maxZoom={2}
            proOptions={{ hideAttribution: true }}
            // Don't mount nodes that are outside the viewport. Critical on
            // canvases with many heavy nodes: each VideoGeneratorNode
            // mounts a <video> element + audio refs, and ReactFlow has no
            // intrinsic culling. A 35-node project with 9 video clips
            // would otherwise allocate decoder state for every clip on
            // load even if the user is panned away. Combined with
            // VideoPreviewPlayer's preload="metadata" this is the main
            // lever against the post-generation "canvas is unresponsive"
            // symptom.
            onlyRenderVisibleElements
            panOnDrag
            panOnScroll
            zoomOnScroll
            zoomOnPinch
            deleteKeyCode={null}
            selectionOnDrag={false}
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={28}
              size={1.2}
              color="rgba(245, 242, 236, 0.06)"
            />
            <Controls
              position="bottom-right"
              showInteractive={false}
              style={{ marginRight: 14, marginBottom: 14 }}
            />
          </ReactFlow>
        </CanvasProviders>
      </ReactFlowProvider>

      {/* v2 styling for ReactFlow controls */}
      <style>{`
        .react-flow__controls-button {
          background: transparent !important;
          border: none !important;
          border-bottom: 1px solid var(--line) !important;
          color: var(--text-2) !important;
          width: 32px !important;
          height: 32px !important;
        }
        .react-flow__controls-button:last-child { border-bottom: 0 !important; }
        .react-flow__controls-button:hover {
          background: rgba(245, 242, 236, 0.05) !important;
          color: var(--text) !important;
        }
        .react-flow__controls-button svg { fill: currentColor !important; }
      `}</style>
    </div>
  )
}

// Type from CanvasContext.addNode — accepted by writeNodeData / addNodeToYDoc.
type NodeDataRecord = Parameters<CanvasContextValue['updateNodeData']>[1]

/**
 * Mounts CanvasContext + UploadContext for the vendored canvas-v2 tree.
 *
 * Must live inside <ReactFlowProvider> because `useReactFlow()` is required
 * for `screenToFlowPosition` (the upload handler computes a viewport-center
 * drop position the same way v1's `handleUploadFromRefSelector` does).
 *
 * The CanvasContextValue is intentionally minimum-viable — it covers the
 * methods the vendored ImageUploadNode + ReferenceSelector + designer-node
 * bottom panels actually call. Stubs for unused methods throw on access so
 * we discover what else needs wiring as we use the canvas.
 */
function CanvasProviders({
  projectId,
  nodesRef,
  setNodes,
  addNodeToYDoc,
  removeNodeFromYDoc,
  addEdgeToYDoc,
  removeEdgeFromYDoc,
  writeNodeData,
  writeNodePosition,
  ydoc,
  children,
}: {
  projectId: string
  nodesRef: React.MutableRefObject<ReactFlowCanvasNode[]>
  setNodes: ReturnType<typeof useNodesState<ReactFlowNodeData>>[1]
  addNodeToYDoc: (
    nodeId: string,
    nodeType: string,
    label: string,
    x: number,
    y: number,
    data?: NodeDataRecord,
    tainted?: boolean,
    parentId?: string,
  ) => void
  removeNodeFromYDoc: (nodeId: string) => void
  addEdgeToYDoc: (edge: {
    source: string
    target: string
    sourceHandle?: string
    targetHandle?: string
    toIndex?: number
    fromIndex?: number
    type?: string
  }) => void
  removeEdgeFromYDoc: (edgeId: string) => void
  writeNodeData: (nodeId: string, updates: NodeDataRecord) => void
  writeNodePosition: (nodeId: string, x: number, y: number) => void
  ydoc: Y.Doc
  children: React.ReactNode
}) {
  const rf = useReactFlow<ReactFlowNodeData, ReactFlowCanvasEdgeData>()

  const canvasValue: CanvasContextValue = useMemo(
    () => ({
      // ── DAG manipulation ───────────────────────────────────────────────
      addNode: (type, position, data, options) => {
        const newNode = createNode(type, position, {
          ...(data ?? {}),
          projectId,
        })
        const typedNewNode: ReactFlowCanvasNode = options?.parentId
          ? ({ ...newNode, parentId: options.parentId } as ReactFlowCanvasNode)
          : (newNode as ReactFlowCanvasNode)

        // Push to React state FIRST so non-serializable fields like
        // `initialFile` (File object) reach ImageUploadNode this render
        // tick. The YDoc write below uses the schema-driven per-field
        // writer, which filters out non-schema keys — so initialFile
        // never lands in YDoc. ImageUploadNode's useRef flag guards
        // against re-firing if the YDoc → React sync later overwrites
        // the React node.
        setNodes((nds) => [...nds, typedNewNode])

        // Schema-typed write to YDoc (label = best-effort display name).
        const label =
          (typeof data?.in_name === 'string' && data.in_name) ||
          (typeof data?.in_label === 'string' && data.in_label) ||
          (typeof data?.in_file_name === 'string' && data.in_file_name) ||
          newNode.id
        addNodeToYDoc(
          newNode.id,
          type,
          label,
          position.x,
          position.y,
          (data ?? {}) as NodeDataRecord,
          true,
          options?.parentId,
        )

        if (options?.autoExecute) {
          void executeNodeApi(projectId, newNode.id).catch((err) =>
            console.warn('autoExecute failed for', newNode.id, err),
          )
        }
        return newNode.id
      },
      addEdge: (source, sourceHandle, target, targetHandle) => {
        // Validate port-type compatibility the same way v1's
        // useCanvasContextValue does. Silently no-op on bad connections
        // so an invalid edge never lands in YDoc and crashes the next
        // agent that snapshots inputs. Logs in dev so the gap is visible.
        const sourceNode = rf.getNode(source)
        const targetNode = rf.getNode(target)
        const sourcePortSchema = sourceNode?.type
          ? nodeSchemas[sourceNode.type]?.outputs.find((p) => p.id === sourceHandle)
          : undefined
        const targetPortSchema = targetNode?.type
          ? nodeSchemas[targetNode.type]?.inputs.find((p) => p.id === targetHandle)
          : undefined
        const sourceType = sourcePortSchema?.type
        const targetType = targetPortSchema?.type
        if (sourceType && targetType && !areTypesCompatible(sourceType, targetType)) {
          console.warn(
            '[canvas] addEdge: incompatible port types',
            { source, sourceHandle, sourceType, target, targetHandle, targetType },
          )
          return
        }
        addEdgeToYDoc({ source, target, sourceHandle, targetHandle })
      },
      removeNode: (nodeId) => removeNodeFromYDoc(nodeId),
      removeEdge: (edgeId) => removeEdgeFromYDoc(edgeId),
      duplicateNode: (nodeId) => {
        // Not implemented in v2 yet — the vendored designer-node bottom
        // panels expose Duplicate buttons; surface the gap loudly so we
        // know when something actually tries to call it.
        console.warn('[canvas] duplicateNode not implemented in v2', nodeId)
        return undefined
      },

      // ── Node data ──────────────────────────────────────────────────────
      updateNodeData: (nodeId, updates) => writeNodeData(nodeId, updates),
      persistNodeData: (nodeId, updates) => writeNodeData(nodeId, updates),
      updateNodeParent: (nodeId, parentId) => {
        console.warn('[canvas] updateNodeParent not implemented in v2', {
          nodeId,
          parentId,
        })
      },
      updateNodePosition: writeNodePosition,
      transact: (fn) => ydoc.transact(fn, 'frontend'),

      // ── Execution ──────────────────────────────────────────────────────
      executeNode: (nodeId) => {
        void executeNodeApi(projectId, nodeId).catch((err) =>
          console.warn('executeNode failed for', nodeId, err),
        )
      },

      // ── State access ───────────────────────────────────────────────────
      getNode: (nodeId) => nodesRef.current.find((n) => n.id === nodeId),
      getNodes: () => nodesRef.current,

      // ── Edge sync (ReferenceSelector uses this to set in_images) ──────
      // Port verbatim from v1's WorkspacePageV2.replacePortEdges — same
      // YDoc shape, same edge-id convention, so the round-trip is
      // identical and existing edges deserialize the same way.
      replacePortEdges: (targetId, targetHandle, newEdges) => {
        const yEdges = ydoc.getMap<Y.Map<unknown>>('edges')
        ydoc.transact(() => {
          const toDelete: string[] = []
          yEdges.forEach((yedge, edgeId) => {
            const tgt = yedge.get('target') as string | undefined
            const tgtH = yedge.get('targetHandle') as string | undefined
            if (tgt === targetId && tgtH === targetHandle) toDelete.push(edgeId)
          })
          for (const id of toDelete) yEdges.delete(id)
          newEdges.forEach((e, i) => {
            const toIndex = e.toIndex ?? i
            const edgeId = `${e.source}.${e.sourceHandle}-${targetId}.${targetHandle}[${toIndex}]`
            const edgeData = new Y.Map<unknown>()
            edgeData.set('source', e.source)
            edgeData.set('target', targetId)
            edgeData.set('type', 'default')
            edgeData.set('sourceHandle', e.sourceHandle)
            edgeData.set('targetHandle', targetHandle)
            edgeData.set('toIndex', toIndex)
            yEdges.set(edgeId, edgeData)
          })
        }, 'frontend')
      },
    }),
    [
      projectId,
      nodesRef,
      setNodes,
      addNodeToYDoc,
      removeNodeFromYDoc,
      addEdgeToYDoc,
      removeEdgeFromYDoc,
      writeNodeData,
      writeNodePosition,
      ydoc,
      rf,
    ],
  )

  // Upload handler — mirrors v1's WorkspacePageV2.handleUploadFromRefSelector.
  // Computes viewport-center position, avoids collision, creates an `image`
  // node with the File + preview-URL on its React data (NOT in YDoc), returns
  // the new node id so ReferenceSelector can wire the edge.
  const handleUpload: UploadFileFn = useCallback(
    (file, type) => {
      const canvasEl = document.querySelector('.react-flow')
      const width = canvasEl?.clientWidth ?? window.innerWidth
      const height = canvasEl?.clientHeight ?? window.innerHeight
      const centerScreen = { x: width / 2, y: height / 2 }
      const flowPos = rf.screenToFlowPosition
        ? rf.screenToFlowPosition(centerScreen)
        : { x: 400, y: 300 }

      // Simple collision-avoidance: nudge 40px down-right up to 10 times.
      const OFFSET_STEP = 40
      let offsetX = 0
      let offsetY = 0
      const existing = nodesRef.current.map((n) => n.position)
      for (let i = 0; i < 10; i++) {
        const tx = flowPos.x + offsetX
        const ty = flowPos.y + offsetY
        const tooClose = existing.some(
          (p) => Math.abs(p.x - tx) < 50 && Math.abs(p.y - ty) < 50,
        )
        if (!tooClose) break
        offsetX += OFFSET_STEP
        offsetY += OFFSET_STEP
      }
      const position: CanvasPosition = {
        x: flowPos.x + offsetX,
        y: flowPos.y + offsetY,
      }

      // Sanity-check: the registry knows this type. Without this an
      // unknown `type` would silently no-op on the canvas while the file
      // sits in browser memory.
      if (!nodeSchemas[type]) {
        console.warn('[upload] no schema for type', type)
      }

      const previewUrl = URL.createObjectURL(file)
      const newId = canvasValue.addNode(type, position, {
        initialFile: file,
        _initialPreviewUrl: previewUrl,
        status: 'running',
        in_file_name: file.name,
      })
      return newId
    },
    [rf, nodesRef, canvasValue],
  )

  return (
    <CanvasContext.Provider value={canvasValue}>
      <UploadContext.Provider value={handleUpload}>
        {children}
      </UploadContext.Provider>
    </CanvasContext.Provider>
  )
}

function ConnectionPill({
  connected, synced, ready, nodeCount, running,
}: { connected: boolean; synced: boolean; ready: boolean; nodeCount: number; running: number }) {
  if (!connected) {
    return (
      <div className="pill">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-text-3" />
        <span>connecting canvas</span>
      </div>
    )
  }
  if (!synced || !ready) {
    return (
      <div className="pill">
        <span className="live-dot" />
        <span>syncing</span>
      </div>
    )
  }
  if (running > 0) {
    return (
      <div className="pill pill--accent">
        <span className="live-dot" />
        <span>canvas · {nodeCount} nodes · {running} running</span>
      </div>
    )
  }
  return (
    <div className="pill pill--accent">
      <span className="live-dot" />
      <span>canvas · {nodeCount} nodes</span>
    </div>
  )
}
